import {
  CPC_SECTIONS,
  JURISDICTION_VALUES,
  LEGAL_STATUS,
  LEGAL_STATUSES,
} from '../consts'
import { dateToString, wrapWithNonNullFallback } from '../utils'
import { z } from 'zod'
import dayjs from 'dayjs'

export const legalStatusSubmitSchema = z
  .enum([
    'granted',
    'active_reinstated',
    'pending',
    '',
    'expired',
    'expired_lifetime',
    'expired_fee_related',
    'expired_',
    'abandoned',
    'withdrawn',
    'revoked',
    'ceased',
  ])
  .nullable()
  .default(null)

export type LegalStatusSubmitValue = z.infer<typeof legalStatusSubmitSchema>

export const multiSelectAPITypeSchema = z
  .object({
    name: z.string(),
    not: z.boolean(),
  })
  .describe('Multi Select API Type Schema')

export const multiSelectAPITypeWithEntitiesSchema = z
  .object({
    name: z.string(),
    not: z.boolean(),
    related_entities: z
      .array(z.object({ name: z.string(), selected: z.boolean() }))
      .optional()
      .nullable()
      .default([]),
  })
  .describe('Multi Select API Type With Entities Schema')

export type MultiSelectAPIType = z.infer<typeof multiSelectAPITypeSchema>

export const apiFilterSchema = z
  .object({
    id: z.string().min(1),
    applicants: wrapWithNonNullFallback(
      z.array(multiSelectAPITypeWithEntitiesSchema).nullable().default(null),
      []
    ),
    backward_citations: z.array(z.string()).default([]),
    cpc_sections: z.array(z.enum(CPC_SECTIONS)).default([]),
    cpc_subclasses: wrapWithNonNullFallback(
      z.array(z.string().min(1).max(15)).nullable().default(null),
      []
    ),
    current_assignees: wrapWithNonNullFallback(
      z.array(multiSelectAPITypeWithEntitiesSchema).nullable().default(null),
      []
    ),
    forward_citations: z.array(z.string().min(1).max(30)).default([]),
    inventors: wrapWithNonNullFallback(
      z.array(multiSelectAPITypeSchema).nullable().default(null),
      []
    ),
    jurisdictions: z.array(z.enum(JURISDICTION_VALUES)).default([]),
    priority_date_from: z.string().nullable().default(null),
    priority_date_to: z.string().nullable().default(null),
    projected_expiration_date_from: z.string().nullable().default(null),
    projected_expiration_date_to: z.string().nullable().default(null),
    publication_date_from: z.string().nullable().default(null),
    publication_date_to: z.string().nullable().default(null),
    application_date_from: z.string().nullable().default(null),
    application_date_to: z.string().nullable().default(null),
    document_numbers: wrapWithNonNullFallback(
      z.array(z.string().min(1).max(30)).nullable().default(null),
      []
    ),
    document_numbers_not: z.boolean().nullable().default(false),
    legal_status: z
      .array(z.string().nullable().default('missing'))
      .default([])
      .transform((statuses) => [
        ...new Set(
          statuses.map((status) => {
            const matchingStatus = Object.values(LEGAL_STATUSES).find(
              (legalStatus) =>
                legalStatus.value === status ||
                legalStatus.statuses.includes(status ?? 'unknown')
            )
            return matchingStatus?.value ?? 'missing'
          })
        ),
      ])
      .pipe(z.array(z.enum(LEGAL_STATUS))),
    is_licensed: z.boolean().nullable().default(null),
    is_litigated: z.boolean().nullable().default(null),
    keyword_opensearch_query: z.unknown().nullable().default(null),
    keyword_text_query: z.string().nullable().default(null),
    publication_type: z
      .array(z.string())
      // Whenever "application" or "grant" is selected, we automatically select "unknown" and
      // "informational" as well But we only want to store "application" or "grant" in the state, so
      // the others are filtered out.
      .transform(
        (publicationTypes) =>
          publicationTypes.find(
            (publicationType) =>
              publicationType === 'application' || publicationType === 'grant'
          ) ?? null
      ),
  })
  .describe('API Filter Schema')

export type ApiFilter = z.infer<typeof apiFilterSchema>

export const filterSchema = apiFilterSchema
  .transform((filters) => {
    const {
      applicants: applicants,
      cpc_sections: cpcSections,
      cpc_subclasses: cpcSubclasses,
      current_assignees: currentAssignees,
      forward_citations: forwardCitations,
      priority_date_from: priorityDateFrom,
      priority_date_to: priorityDateTo,
      projected_expiration_date_from: projectedExpirationDateFrom,
      projected_expiration_date_to: projectedExpirationDateTo,
      application_date_from: applicationDateFrom,
      application_date_to: applicationDateTo,
      legal_status: legalStatus,
      is_licensed: isLicensed,
      is_litigated: isLitigated,
      keyword_text_query: keywordTextQuery,
      backward_citations: backwardCitations,
      publication_type: publicationType,
      document_numbers: documentNumbers,
      document_numbers_not: documentNumbersNot,
      publication_date_from: publicationDateFrom,
      publication_date_to: publicationDateTo,
      ...filterFields
    } = filters

    return {
      ...filterFields,
      applicants: applicants.map(({ name, not, related_entities }) => ({
        name,
        not,
        relatedEntities: related_entities,
      })),
      cpcSections,
      cpcSubclasses,
      currentAssignees: currentAssignees.map(
        ({ name, not, related_entities }) => ({
          name,
          not,
          relatedEntities: related_entities,
        })
      ),
      forwardCitations,
      priorityDateFrom: priorityDateFrom
        ? dayjs(priorityDateFrom, 'YYYY-MM-DD').toDate()
        : null,
      priorityDateTo: priorityDateTo
        ? dayjs(priorityDateTo, 'YYYY-MM-DD').toDate()
        : null,
      projectedExpirationDateFrom: projectedExpirationDateFrom
        ? dayjs(projectedExpirationDateFrom, 'YYYY-MM-DD').toDate()
        : null,
      projectedExpirationDateTo: projectedExpirationDateTo
        ? dayjs(projectedExpirationDateTo, 'YYYY-MM-DD').toDate()
        : null,
      applicationDateFrom: applicationDateFrom
        ? dayjs(applicationDateFrom, 'YYYY-MM-DD').toDate()
        : null,
      applicationDateTo: applicationDateTo ? new Date(applicationDateTo) : null,
      publicationDateFrom: publicationDateFrom
        ? dayjs(publicationDateFrom, 'YYYY-MM-DD').toDate()
        : null,
      publicationDateTo: publicationDateTo
        ? dayjs(publicationDateTo, 'YYYY-MM-DD').toDate()
        : null,
      legalStatus,
      isLicensed,
      isLitigated,
      keywordTextQuery,
      backwardCitations,
      publicationType,
      documentNumbers,
      documentNumbersNot: documentNumbersNot ?? false,
    }
  })
  .describe('Transformed Filter Schema')

export type Filter = z.infer<typeof filterSchema>

export const emptyFilter: Omit<Filter, 'id'> = {
  applicants: [],
  cpcSections: [],
  cpcSubclasses: [],
  currentAssignees: [],
  forwardCitations: [],
  priorityDateFrom: null,
  priorityDateTo: null,
  projectedExpirationDateFrom: null,
  projectedExpirationDateTo: null,
  applicationDateFrom: null,
  applicationDateTo: null,
  legalStatus: [],
  isLicensed: null,
  isLitigated: null,
  keywordTextQuery: null,
  backwardCitations: [],
  publicationType: null,
  documentNumbers: [],
  documentNumbersNot: false,
  keyword_opensearch_query: null,
  inventors: [],
  jurisdictions: [],
  publicationDateFrom: null,
  publicationDateTo: null,
}

export const updateFilterSchema = apiFilterSchema.extend({
  legal_status: legalStatusSubmitSchema.array(),

  // Whenever "application" or "grant" is selected, we automatically select "unknown" and
  // "informational" as well But we only want to store "application" or "grant" in the state, so the
  // others are filtered out.
  publication_type: z
    .string()
    .nullable()
    .transform((value) => (value ? [value, 'unknown', 'informational'] : [])),
  id: z.string().min(1).optional(),
})

/**
 * Is missing id and keyword_opensearch_query.
 */
export const transformClientFilterToApiFilter = ({
  jurisdictions,
  applicants,
  currentAssignees,
  inventors,
  cpcSections,
  cpcSubclasses,
  legalStatus,
  priorityDateFrom,
  priorityDateTo,
  projectedExpirationDateFrom,
  projectedExpirationDateTo,
  applicationDateFrom,
  applicationDateTo,
  forwardCitations,
  isLitigated,
  isLicensed,
  publicationType,
  keywordTextQuery,
  documentNumbers,
  documentNumbersNot,
  publicationDateFrom,
  publicationDateTo,
  backwardCitations,
}: Omit<Filter, 'id' | 'keyword_opensearch_query'>) => ({
  jurisdictions,
  applicants: applicants.map((applicant) => ({
    name: applicant.name,
    not: applicant.not,
    related_entities: applicant.relatedEntities,
  })),
  current_assignees: currentAssignees.map((assignee) => ({
    name: assignee.name,
    not: assignee.not,
    related_entities: assignee.relatedEntities,
  })),
  inventors,
  cpc_sections: cpcSections,
  cpc_subclasses: cpcSubclasses,
  legal_status: legalStatus.map((status) =>
    status === 'missing' ? null : status
  ),
  priority_date_from: dateToString(priorityDateFrom),
  priority_date_to: dateToString(priorityDateTo),
  projected_expiration_date_from: dateToString(projectedExpirationDateFrom),
  projected_expiration_date_to: dateToString(projectedExpirationDateTo),
  application_date_from: dateToString(applicationDateFrom),
  application_date_to: dateToString(applicationDateTo),
  forward_citations: forwardCitations,
  is_litigated: isLitigated,
  is_licensed: isLicensed,
  publication_type: publicationType,
  keyword_text_query: keywordTextQuery,
  document_numbers: documentNumbers,
  document_numbers_not: documentNumbersNot,
  backward_citations: backwardCitations,
  publication_date_from: dateToString(publicationDateFrom),
  publication_date_to: dateToString(publicationDateTo),
})
